import _ from 'lodash';
import { SPACES } from 'common/constants/sizes';
import { getColor } from 'common/constants/_colors';
import { PROVIDERS_MAP } from 'common/constants/constants';
import Input from 'components/base/Input';
import {
  Flex,
  Padding,
  Separator,
  TableNoVerticalLines
} from 'components/base/layout-components';
import Table from 'components/base/Table';
import Text from 'components/base/Text';
import SnackBarMessage from 'components/Message';
import { usePMEntities } from './hooks/usePMEntities';
import usePmEntitiesTable from './usePmEntitiesTable';
import usePmEntitiesFilters from './usePmEntitiesFilters';
import SelectedEntitiesCounter from './components/SelectedEntitiesCounter';
import FilterChip from './components/FilterChip';
import { DeviationError } from 'common/constants/error-types';
import { LBButton } from '@linearb/buzz';

const PMEntities = ({
  toggleSnackBar,
  boards,
  providerEntities,
  providerEntitiesWithExtras,
  sensorAuth,
  projects = [],
  isLoading,
  entityName
}) => {
  const pmToolConfig = PROVIDERS_MAP[sensorAuth.sensor_name.toLowerCase()];
  const entityNamePlural = _.get(
    pmToolConfig,
    `entity_names_plural.${entityName}`,
    pmToolConfig.entity_name_plural
  );

  const { entities, selectedEntities, isSaveEnabled, onSaveChangesClick } =
    usePMEntities({
      boards,
      projects,
      providerEntities,
      providerEntitiesWithExtras,
      pmToolConfig,
      entityName
    });

  const {
    filteredEntities,
    searchTerm,
    setSearchTerm,
    isSelectedOnly,
    toggleIsSelectedOnly,
    isFilterLoading
  } = usePmEntitiesFilters({
    entities,
    selectedEntities,
    entityName
  });
  const pmTable = usePmEntitiesTable({
    pmToolConfig,
    entityName,
    entities,
    filteredEntities,
    selectedEntities,
    isSelectedOnly
  });

  const onSave = async () => {
    try {
      await onSaveChangesClick();
      toggleSnackBar({
        message: (
          <SnackBarMessage
            message={`Success! You connected your ${pmToolConfig.name} ${entityNamePlural}! it might take some time to update the data`}
          />
        ),
        variant: 'success'
      });
    } catch (e) {
      const message =
        e instanceof DeviationError
          ? e.message
          : 'Failed to update settings, please try again later';
      toggleSnackBar({
        message: <SnackBarMessage message={message} />,
        variant: 'error'
      });
    }
  };

  return (
    <>
      <Flex items='center'>
        <Flex direction='column'>
          <Text
            size='large'
            weight='500'
            textTransform='uppercase'
            pb={SPACES.small}
          >
            {pmToolConfig.name} Settings
          </Text>
          <Text size='medium' color='shuttleGray' pb={SPACES.small}>
            Company reporting time frame is set to 1 week
          </Text>
        </Flex>
        <Flex grow='1' items='center' justify='flex-end'>
          <SelectedEntitiesCounter
            entityName={entityName}
            count={selectedEntities.values.length}
            toggleIsSelectedOnly={toggleIsSelectedOnly}
          />
          <LBButton
            data-cy='SaveSettings'
            disabled={!isSaveEnabled}
            onClick={() => onSave()}
          >
            Save Settings
          </LBButton>
        </Flex>
      </Flex>
      <Separator
        width='100%'
        height='1px'
        color={getColor('catskillWhite')}
        margin='1rem 0 2rem 0'
      ></Separator>
      <Text
        size='large'
        weight='500'
        textTransform='uppercase'
        pb={SPACES.small}
      >
        {entityName}s
      </Text>
      <Text size='medium' color='shuttleGray' pb={SPACES.small}>
        Select which {entityName} to include on your LinearB activity:
      </Text>
      <Flex pt='small' pb='medium' items='center'>
        <Padding r='small'>
          <Input
            type='search'
            height='2.5rem'
            width='30rem'
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={`Search for ${entityName}`}
          />
        </Padding>
        {isSelectedOnly && (
          <FilterChip
            text={`Selected ${entityName}s`}
            onClose={toggleIsSelectedOnly}
          />
        )}
      </Flex>
      <TableNoVerticalLines>
        <Table
          isLoading={isLoading || isFilterLoading}
          withPagination={pmTable.shouldPaginate}
          clientSidePagination={pmTable.shouldPaginate}
          columns={pmTable.cols}
          data={pmTable.rows}
        ></Table>
      </TableNoVerticalLines>
    </>
  );
};

export default PMEntities;
